import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const WorkstreamBoard = () => {
  const { data: workstreams, loading, error, retry } = useAsyncData('/api/workstreams');
  const { isStale, lastUpdated } = useStaleWarning(workstreams);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <section className="panel ws-board" aria-busy="true">
          <h2>Active Workstreams</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </section>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <section className="panel ws-board" aria-live="assertive">
          <h2>Active Workstreams</h2>
          <p className="error-msg">Failed to load workstreams.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </section>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <section className="panel ws-board" aria-label="Workstreams" aria-busy="false">
        <div className="panel-header">
          <h2>Active Workstreams</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh workstreams">
              ↻
            </button>
          </div>
        </div>
        <div className="ws-list" role="list" aria-label="Active workstreams">
          {(workstreams || []).length === 0 && <p className="empty">No active workstreams</p>}
          {(workstreams || []).map((ws) => (
            <div className="ws-row" role="listitem" key={ws.name}>
              <h3>{ws.name}</h3>
              <p>{ws.detail}</p>
              <progress value={ws.progress} max={100} aria-label={`${ws.name} progress`} />
              <span className="ws-progress">{ws.progress}%</span>
            </div>
          ))}
        </div>
      </section>
    </ErrorBoundary>
  );
};

export default WorkstreamBoard;
